'use client';

import Link from 'next/link';
import { CheckCircle } from 'lucide-react';
import { compliancePricingPlans, type CompliancePricingPlan } from '@/lib/data/compliance-pricing';

// Re-export for backward compatibility
export { compliancePricingPlans, type CompliancePricingPlan };

type CompliancePricingSectionProps = {
  title?: string;
  description?: string;
};

export default function CompliancePricingSection({
  title = 'Compliance Programs',
  description = "Choose the level of guidance and automation that fits your organization’s maturity. All prices are starting at and vary based on scope, size, and framework requirements."
}: CompliancePricingSectionProps) {
  return (
    <section className="py-20 px-4">
      <div className="container mx-auto max-w-7xl">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold mb-6 text-gray-900 dark:text-gray-100">{title}</h2>
          {description && (
            <p className="text-xl text-gray-600 dark:text-gray-400 max-w-3xl mx-auto">
              {description}
            </p>
          )}
        </div>

        <div className="grid md:grid-cols-2 xl:grid-cols-4 gap-10">
          {compliancePricingPlans.map((plan, index) => {
            const planSlug = plan.name
              .toLowerCase()
              .replace(/[^a-z0-9\s-]/g, '')
              .trim()
              .replace(/\s+/g, '-');

            return (
              <div
                key={index}
                className={`relative bg-white dark:bg-gray-800 rounded-2xl p-8 flex flex-col ${
                  plan.featured
                    ? 'border-4 border-blue-600 shadow-2xl transform xl:scale-105'
                    : 'border-2 border-gray-200 dark:border-gray-700 shadow-lg'
                }`}
              >
              {plan.badge && (
                <div className="absolute -top-4 left-1/2 -translate-x-1/2 transform">
                  <span className="bg-gradient-to-r from-blue-600 to-blue-700 text-white px-6 py-2 rounded-full text-sm font-bold shadow-lg">
                    {plan.badge}
                  </span>
                </div>
              )}

              <div className="mb-6">
                <span className="inline-flex items-center text-xs font-semibold tracking-wider uppercase text-blue-600 dark:text-blue-400 mb-3">
                  {plan.stage}
                </span>
                <h3 className="text-2xl font-bold mb-2 text-gray-900 dark:text-gray-100">{plan.name}</h3>
                <div className="mb-3">
                  <span className="block text-xs font-semibold uppercase tracking-widest text-blue-600 dark:text-blue-400">
                    Starting at
                  </span>
                  <div className="flex items-baseline gap-2">
                    <span className="text-4xl font-bold text-blue-600 dark:text-blue-400">{plan.price}</span>
                    {plan.period && <span className="text-sm font-semibold text-gray-600 dark:text-gray-400">{plan.period}</span>}
                  </div>
                  {plan.tagline && (
                    <p className="text-sm font-semibold text-gray-600 dark:text-gray-400 mt-1">{plan.tagline}</p>
                  )}
                </div>
                <p className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1">{plan.description}</p>
                {plan.subtitle && <p className="text-sm text-gray-600 dark:text-gray-400">{plan.subtitle}</p>}
              </div>

              <div className="mb-6 flex-1">
                <ul className="space-y-3">
                  {plan.includes.map((item, i) => (
                    <li key={i} className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400 flex-shrink-0 mt-0.5" />
                      <span className={`text-sm ${item.includes('Everything') ? 'font-semibold text-gray-900 dark:text-gray-100' : 'text-gray-700 dark:text-gray-300'}`}>
                        {item}
                      </span>
                    </li>
                  ))}
                </ul>

                {plan.notIncluded && (
                  <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                    <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 mb-2">Not included:</p>
                    <ul className="space-y-2">
                      {plan.notIncluded.map((item, i) => (
                        <li key={i} className="text-xs text-gray-500 dark:text-gray-400">
                          • {item}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>

              <Link
                href={{ pathname: '/contact', query: { plan: planSlug, source: 'hipaa-compliance' } }}
                className={`not-prose block w-full text-center py-3 rounded-xl font-bold transition-all mt-auto ${
                  plan.featured
                    ? 'bg-blue-600 text-white hover:bg-blue-700'
                    : 'bg-gray-100 dark:bg-gray-800 text-gray-900 dark:text-gray-100 hover:bg-gray-200 dark:hover:bg-gray-700'
                }`}
              >
                {plan.cta}
              </Link>
            </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
